import { createContext, Fragment, useCallback, useContext, useEffect, useLayoutEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../api';
import { useSession } from '../auth/session';
import { isBilltoro } from '../produkt';
import { applyLang, currentLang, isLang, LANGS, load, loaded, t, type Lang } from './t';

// Sprache wählen und anwenden; die Übersetzung selbst steht in t.ts.
export { currentLang, LANGS, t, tl, tx, type Lang } from './t';

// Wie t(), aber Platzhalter dürfen Elemente sein, damit die Wortstellung der Sprache bleibt:
// tn('Wir haben Ihnen einen Code an {email} geschickt.', { email: <b>{to}</b> })
// Anfangsbuchstabe als Symbol eines Bereichs (Übersicht → Ü, Overview → O). Arabisch behält den deutschen Buchstaben,
// weil dort fast jedes Wort mit „ال“ beginnt und alle Symbole gleich aussähen.
export function initialOf(label: string): string {
  const l = currentLang();
  return (l === 'ar' ? label : t(label)).charAt(0).toLocaleUpperCase(l === 'tr' ? 'tr' : l);
}

export function tn(de: string, vars: Record<string, ReactNode>): ReactNode {
  return t(de)
    .split(/(\{\w+\})/)
    .map((part, i) => {
      const m = /^\{(\w+)\}$/.exec(part);
      return <Fragment key={i}>{m && m[1]! in vars ? vars[m[1]!] : part}</Fragment>;
    });
}

// Gewählte Sprache auf diesem Gerät; ohne Wahl die Sprache des Geräts, sonst Deutsch
const KEY = 'nt-lang';
function stored(): Lang | null {
  try {
    const v = localStorage.getItem(KEY);
    return isLang(v) ? v : null;
  } catch {
    return null;
  }
}
function store(lang: Lang) {
  try {
    localStorage.setItem(KEY, lang);
  } catch {
    // privates Fenster: gilt dann nur bis zum Neuladen
  }
}
function deviceLang(): Lang {
  for (const l of navigator.languages ?? [navigator.language]) {
    const k = l.slice(0, 2).toLowerCase();
    if (isLang(k)) return k;
  }
  return 'de';
}

interface LangValue {
  lang: Lang;
  setLang(lang: Lang): void;
}
const LangContext = createContext<LangValue>({ lang: 'de', setLang: () => {} });
const BILLTORO = isBilltoro();

export function LangProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { me } = useSession();
  const [lang, setLangState] = useState<Lang>(() => stored() ?? deviceLang());
  const [, setReady] = useState(0);
  // Die Kanzlei bleibt Deutsch, auch wenn auf diesem Gerät eine andere Sprache gewählt ist. Billtoro gibt es nur auf
  // Deutsch (Firmen in Deutschland).
  const eff: Lang = BILLTORO || pathname.startsWith('/kanzlei') ? 'de' : lang;

  useEffect(() => {
    if (!loaded[eff]) void load(eff).then(() => setReady((n) => n + 1));
  }, [eff]);

  // Nach der Anmeldung: Wahl auf diesem Gerät gilt und kommt ans Konto; ohne Wahl die Sprache vom Konto.
  // Nur für Mandanten-Logins, die Kanzlei arbeitet auf Deutsch. Billtoro: keine Wahl, nichts ans Konto.
  const userId = me?.user.kind === 'client' && !BILLTORO ? me.user.id : undefined;
  const userLang = me?.user.lang ?? null;
  useEffect(() => {
    if (!userId) return;
    const mine = stored();
    if (mine) {
      if (mine !== userLang) api.post('/api/auth/lang', { lang: mine }).catch(() => {});
    } else if (userLang) {
      store(userLang);
      setLangState(userLang);
    } else {
      api.post('/api/auth/lang', { lang }).catch(() => {});
    }
    // nur einmal je Anmeldung
  }, [userId]);

  const setLang = useCallback(
    (l: Lang) => {
      store(l);
      setLangState(l);
      if (userId) api.post('/api/auth/lang', { lang: l }).catch(() => {});
    },
    [userId],
  );

  const ready = loaded[eff];
  useLayoutEffect(() => {
    if (!ready) return;
    document.documentElement.lang = eff;
    document.documentElement.dir = eff === 'ar' ? 'rtl' : 'ltr';
  }, [eff, ready]);

  if (!ready) return <div className="spinner" aria-label="…" />;
  applyLang(eff);
  return (
    <LangContext.Provider value={{ lang, setLang }}>
      {/* neu zeichnen, wenn die Sprache wechselt: Alle Texte kommen über t() */}
      <Fragment key={eff}>{children}</Fragment>
    </LangContext.Provider>
  );
}

export function useLang(): LangValue {
  return useContext(LangContext);
}

// Umschalter: Sprache in der eigenen Sprache benannt, damit jeder seine findet
export function LangSwitch({ className = 'langsw' }: { className?: string }) {
  const { lang, setLang } = useLang();
  // Billtoro nur auf Deutsch
  if (BILLTORO) return null;
  return (
    <label className={className}>
      <span aria-hidden="true">🌐</span>
      <select aria-label="Sprache · Language · Dil · اللغة" value={lang} onChange={(e) => setLang(e.target.value as Lang)}>
        {LANGS.map((l) => (
          <option key={l.key} value={l.key} lang={l.key}>
            {l.label}
          </option>
        ))}
      </select>
    </label>
  );
}
